import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import ModalCloseButton from '../../shared/components/modals/ModalCloseButton';
import { formatPhoneNumber, validateIndianPhoneNumber } from '../../utils/phoneFormatter';
import { API_ENDPOINTS } from '../../config/apiConfig';
import './LocationsPage.css';
import '../../features/admin/components/UserFormModal.css';

function ConfirmFact({ label, value, wide = false }) {
  if (value == null || value === '') return null;
  return (
    <div className={`create-confirm-fact${wide ? ' create-confirm-fact--wide' : ''}`}>
      <span className="create-confirm-fact-label">{label}</span>
      <span className="create-confirm-fact-value">{value}</span>
    </div>
  );
}

export default function LocationsCreateWizard({ isOpen, onClose, onSuccess, token }) {
  const [createFormData, setCreateFormData] = useState({
    name: '',
    location_code: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    admin_username: '',
    admin_employee_id: '',
    admin_full_name: '',
    admin_email: '',
    admin_contact_number: ''
  });
  const [employeeIdMode, setEmployeeIdMode] = useState('manual');
  const [currentStep, setCurrentStep] = useState(1);
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdLocationId, setCreatedLocationId] = useState(null);
  const [createLocationStatus, setCreateLocationStatus] = useState('idle');
  const [createLocationError, setCreateLocationError] = useState('');
  const [createUserStatus, setCreateUserStatus] = useState('idle');
  const [createUserError, setCreateUserError] = useState('');

  useEffect(() => {
    const mode = process.env.REACT_APP_EMPLOYEE_ID_MODE || 'manual';
    setEmployeeIdMode(mode.toLowerCase());
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow || 'unset';
      document.documentElement.style.overflow = previousHtmlOverflow || 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (currentStep !== 4) return;

    if (!createdLocationId && createLocationStatus === 'idle') {
      void startCreateLocation();
      return;
    }

    if (createLocationStatus === 'success' && createUserStatus === 'idle') {
      void startCreateAdminUser();
    }
  }, [isOpen, currentStep, createdLocationId, createLocationStatus, createUserStatus]);

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;

    let finalValue = value;
    if (name === 'contact_phone' || name === 'admin_contact_number') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      if (digits.length === 1 && digits === '0') {
        finalValue = '';
      } else if (digits.length > 1 && digits.startsWith('0')) {
        finalValue = '';
      } else {
        finalValue = digits;
      }
    } else {
      finalValue = value;
    }

    setCreateFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));

    if (name === 'name' && !createFormData.location_code) {
      const location_code = value.toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setCreateFormData(prev => ({ ...prev, location_code }));
    }
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!createFormData.name || !createFormData.location_code || !createFormData.contact_email || !createFormData.address || !createFormData.city || !createFormData.state || !createFormData.country) {
        setCreateError('Please fill in all required location details');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createFormData.contact_email)) {
        setCreateError('Please enter a valid contact email address');
        return false;
      }
      if (!/^[a-zA-Z0-9-]+$/.test(createFormData.location_code)) {
        setCreateError('Location code can only contain letters, numbers, and hyphens');
        return false;
      }
      if (createFormData.contact_phone) {
        const contactValidation = validateIndianPhoneNumber(createFormData.contact_phone);
        if (!contactValidation.isValid) {
          setCreateError(`Invalid phone number: ${contactValidation.error}`);
          return false;
        }
      }
    }

    if (step === 2) {
      if (!createFormData.admin_username || !createFormData.admin_full_name || !createFormData.admin_email) {
        setCreateError('Please fill in all required admin user details');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createFormData.admin_email)) {
        setCreateError('Please enter a valid admin email address');
        return false;
      }

      if (employeeIdMode === 'manual' && !createFormData.admin_employee_id?.trim()) {
        setCreateError('Employee ID is required to create admin user');
        return false;
      }

      if (!createFormData.admin_contact_number) {
        setCreateError('Contact number is required to create admin user');
        return false;
      }

      const adminContactValidation = validateIndianPhoneNumber(createFormData.admin_contact_number);
      if (!adminContactValidation.isValid) {
        setCreateError(`Invalid contact number: ${adminContactValidation.error}`);
        return false;
      }
    }

    setCreateError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(Math.max(1, currentStep - 1));
    setCreateError('');
  };

  const goToStepForEdit = (step) => {
    if (step === 1) {
      setCreatedLocationId(null);
      setCreateLocationStatus('idle');
      setCreateLocationError('');
      setCreateUserStatus('idle');
      setCreateUserError('');
      setIsCreating(false);
    }

    if (step === 2) {
      setCreateUserStatus('idle');
      setCreateUserError('');
      setIsCreating(false);
    }

    setCurrentStep(step);
    setCreateError('');
  };

  const resetCreateWizard = () => {
    setCurrentStep(1);
    setCreateError('');
    setIsCreating(false);
    setCreatedLocationId(null);
    setCreateLocationStatus('idle');
    setCreateLocationError('');
    setCreateUserStatus('idle');
    setCreateUserError('');
    setCreateFormData({
      name: '',
      location_code: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      admin_username: '',
      admin_employee_id: '',
      admin_full_name: '',
      admin_email: '',
      admin_contact_number: ''
    });
  };

  useEffect(() => {
    if (isOpen) {
      resetCreateWizard();
    }
  }, [isOpen]);

  const startCreateLocation = async () => {
    setIsCreating(true);
    setCreateLocationStatus('running');
    setCreateLocationError('');
    setCreateUserStatus('idle');
    setCreateUserError('');

    try {
      const locationPayload = {
        name: createFormData.name,
        location_code: createFormData.location_code,
        contact_email: createFormData.contact_email,
        contact_phone: createFormData.contact_phone || undefined,
        address: createFormData.address || undefined,
        city: createFormData.city || undefined,
        state: createFormData.state || undefined,
        country: createFormData.country || undefined
      };

      const response = await axios.post(API_ENDPOINTS.LOCATIONS.CREATE, locationPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const newLocationId = response.data?.id;
      if (!newLocationId) {
        throw new Error('Location created but no id returned');
      }

      setCreatedLocationId(newLocationId);
      setCreateLocationStatus('success');
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Failed to create location';
      setCreateLocationError(msg);
      setCreateLocationStatus('error');
      setIsCreating(false);
    }
  };

  const startCreateAdminUser = async () => {
    if (!createdLocationId) return;

    setIsCreating(true);
    setCreateUserStatus('running');
    setCreateUserError('');

    try {
      const adminUserPayloadBase = {
        username: createFormData.admin_username,
        email: createFormData.admin_email,
        full_name: createFormData.admin_full_name,
        role: 'admin',
        location_ids: [createdLocationId]
      };

      adminUserPayloadBase.contact_number = createFormData.admin_contact_number.replace(/\D/g, '').slice(0, 10);

      const adminUserPayload = employeeIdMode === 'auto'
        ? adminUserPayloadBase
        : {
          ...adminUserPayloadBase,
          employee_id: createFormData.admin_employee_id?.trim()
        };

      await axios.post(API_ENDPOINTS.USERS.CREATE, adminUserPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setCreateUserStatus('success');

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Failed to create admin user';
      setCreateUserError(msg);
      setCreateUserStatus('error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleContinueToConfirm = () => {
    if (currentStep !== 3) return;

    setCreateError('');
    setCurrentStep(4);
  };

  const handleCancelCreate = () => {
    resetCreateWizard();
    onClose();
  };

  const handleFinish = () => {
    resetCreateWizard();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="locations-create-modal-overlay" role="presentation">
      <div
        className="locations-create-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Create new location"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="locations-create-modal-header">
          <h2>Create New Location</h2>
          <ModalCloseButton
            onClick={handleCancelCreate}
            disabled={isCreating}
            title="Close"
          />
        </div>

        <div className="locations-create-modal-body">
          <div className="progress-bar-create">
            <div
              className={[
                'progress-step',
                currentStep > 1 ? 'is-completed' : '',
                currentStep === 1 ? 'is-current' : '',
                currentStep < 1 ? 'is-pending' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="step-number">1</div>
              <div className="step-label">Location</div>
            </div>
            <div className={`progress-line ${currentStep > 1 ? 'is-completed' : 'is-pending'}`} />
            <div
              className={[
                'progress-step',
                currentStep > 2 ? 'is-completed' : '',
                currentStep === 2 ? 'is-current' : '',
                currentStep < 2 ? 'is-pending' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="step-number">2</div>
              <div className="step-label">Admin User</div>
            </div>
            <div className={`progress-line ${currentStep > 2 ? 'is-completed' : 'is-pending'}`} />
            <div
              className={[
                'progress-step',
                currentStep > 3 ? 'is-completed' : '',
                currentStep === 3 ? 'is-current' : '',
                currentStep < 3 ? 'is-pending' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="step-number">3</div>
              <div className="step-label">Review</div>
            </div>
            <div className={`progress-line ${currentStep > 3 ? 'is-completed' : 'is-pending'}`} />
            <div
              className={[
                'progress-step',
                currentStep > 4 ? 'is-completed' : '',
                currentStep === 4 ? 'is-current' : '',
                currentStep < 4 ? 'is-pending' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="step-number">4</div>
              <div className="step-label">Confirm</div>
            </div>
          </div>

          {createError && <div className="locations-create-error">{createError}</div>}

          <form
            onSubmit={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === 'NumpadEnter') && currentStep < 3) {
                e.preventDefault();
                handleNext();
              }
              if ((e.key === 'Enter' || e.key === 'NumpadEnter') && currentStep === 3) {
                e.preventDefault();
              }
            }}
            className="create-form-inline"
          >
              {currentStep === 1 && (
                <div className="form-step-create">
                  <h4>Location Details</h4>

                  <div className="form-row-create two-col">
                    <div className="form-group-create">
                      <label>Location Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={createFormData.name}
                        onChange={handleCreateFormChange}
                        required
                        placeholder="e.g., Main Campus, North Branch"
                        autoFocus
                      />
                    </div>

                    <div className="form-group-create">
                      <label>Location Code *</label>
                      <input
                        type="text"
                        name="location_code"
                        value={createFormData.location_code}
                        onChange={handleCreateFormChange}
                        required
                        placeholder="e.g., Main-Campus, North-Branch"
                        pattern="[a-zA-Z0-9-]+"
                      />
                      <small>Letters, numbers, and hyphens (case-insensitive)</small>
                    </div>
                  </div>

                  <div className="form-row-create two-col">
                    <div className="form-group-create">
                      <label>Contact Email *</label>
                      <input
                        type="email"
                        name="contact_email"
                        value={createFormData.contact_email}
                        onChange={handleCreateFormChange}
                        required
                        placeholder="contact@hospital.com"
                      />
                    </div>

                    <div className="form-group-create">
                      <label>Contact Phone</label>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 auto', minWidth: '200px', display: 'flex', flexDirection: 'column' }}>
                          <input
                            type="tel"
                            name="contact_phone"
                            value={createFormData.contact_phone}
                            onChange={handleCreateFormChange}
                            maxLength="10"
                            placeholder="e.g., 9876543210"
                          />
                          <small style={{ whiteSpace: 'normal', overflow: 'visible', lineHeight: '1.3', marginTop: '0.25rem' }}>For SMS/WhatsApp notifications</small>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', justifyContent: 'flex-start', minWidth: '160px', flex: '1 1 200px' }}>
                          {(() => {
                            const digits = createFormData.contact_phone ? createFormData.contact_phone.replace(/\D/g, '') : '';
                            const validation = createFormData.contact_phone ? validateIndianPhoneNumber(createFormData.contact_phone) : { isValid: true, error: null };
                            const statusColor = createFormData.contact_phone && !validation.isValid ? '#d32f2f' : digits.length === 10 ? '#27ae60' : '#f59e0b';
                            const statusText = createFormData.contact_phone ? `${digits.length}/10` : '-';
                            const statusIcon = !createFormData.contact_phone ? '-' : validation.isValid ? '✓' : '✗';
                            return (
                              <>
                                <small style={{ color: statusColor, fontWeight: '600', fontSize: '0.85rem', textAlign: 'center', lineHeight: '1.4', marginTop: '0.5rem' }}>
                                  {statusIcon} {statusText}
                                </small>
                                {createFormData.contact_phone && !validation.isValid && (
                                  <small style={{ color: '#d32f2f', fontSize: '0.75rem', textAlign: 'center', lineHeight: '1.3', fontWeight: '500', whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' }}>
                                    {validation.error}
                                  </small>
                                )}
                                {createFormData.contact_phone && validation.isValid && (
                                  <small style={{ color: '#666', fontSize: '0.8rem', whiteSpace: 'nowrap', lineHeight: '1.2', textAlign: 'center' }}>
                                    {formatPhoneNumber(createFormData.contact_phone)}
                                  </small>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group-create">
                    <label>Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={createFormData.address}
                      onChange={handleCreateFormChange}
                      required
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="form-row-create">
                    <div className="form-group-create">
                      <label>City *</label>
                      <input
                        type="text"
                        name="city"
                        value={createFormData.city}
                        onChange={handleCreateFormChange}
                        required
                        placeholder="City"
                      />
                    </div>

                    <div className="form-group-create">
                      <label>State *</label>
                      <input
                        type="text"
                        name="state"
                        value={createFormData.state}
                        onChange={handleCreateFormChange}
                        required
                        placeholder="State"
                      />
                    </div>

                    <div className="form-group-create">
                      <label>Country *</label>
                      <input
                        type="text"
                        name="country"
                        value={createFormData.country}
                        onChange={handleCreateFormChange}
                        required
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="form-step-create">
                  <h4>Administrator Account</h4>
                  <p className="step-description">Create the first admin user for this location</p>

                  <div className="user-form-modal user-form-modal-embed">
                    {employeeIdMode === 'auto' && (
                      <div className="user-form-notice">
                        Employee ID will be automatically generated during account creation
                      </div>
                    )}

                    <div className="user-form-section-title">Personal Information</div>

                    {employeeIdMode === 'manual' ? (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="admin_employee_id">Employee ID *</label>
                          <input
                            type="text"
                            id="admin_employee_id"
                            name="admin_employee_id"
                            value={createFormData.admin_employee_id || ''}
                            onChange={handleCreateFormChange}
                            required
                            placeholder="e.g., EMP12345, ACME-E001"
                            autoFocus
                          />
                          <small className="user-form-hint">
                            Provide the employee ID from your HRIS or external system
                          </small>
                        </div>
                        <div className="form-group">
                          <label htmlFor="admin_full_name">Full Name *</label>
                          <input
                            type="text"
                            id="admin_full_name"
                            name="admin_full_name"
                            value={createFormData.admin_full_name}
                            onChange={handleCreateFormChange}
                            required
                            placeholder="John Doe"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="admin_full_name">Full Name *</label>
                          <input
                            type="text"
                            id="admin_full_name"
                            name="admin_full_name"
                            value={createFormData.admin_full_name}
                            onChange={handleCreateFormChange}
                            required
                            placeholder="John Doe"
                            autoFocus
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="admin_email">Email Address *</label>
                          <input
                            type="email"
                            id="admin_email"
                            name="admin_email"
                            value={createFormData.admin_email}
                            onChange={handleCreateFormChange}
                            required
                            placeholder="john.smith@dhi.com"
                          />
                        </div>
                      </div>
                    )}

                    {employeeIdMode === 'manual' ? (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="admin_email">Email Address *</label>
                          <input
                            type="email"
                            id="admin_email"
                            name="admin_email"
                            value={createFormData.admin_email}
                            onChange={handleCreateFormChange}
                            required
                            placeholder="john.smith@dhi.com"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="admin_contact_number">Contact Number *</label>
                          <input
                            type="tel"
                            id="admin_contact_number"
                            name="admin_contact_number"
                            value={createFormData.admin_contact_number || ''}
                            onChange={handleCreateFormChange}
                            required
                            maxLength="10"
                            placeholder="e.g., 9876543210"
                          />
                          {(() => {
                            const digits = createFormData.admin_contact_number ? createFormData.admin_contact_number.replace(/\D/g, '') : '';
                            const validation = createFormData.admin_contact_number ? validateIndianPhoneNumber(createFormData.admin_contact_number) : { isValid: true, error: null };
                            const statusColor = createFormData.admin_contact_number && !validation.isValid ? '#d32f2f' : digits.length === 10 ? '#27ae60' : '#f59e0b';
                            const statusIcon = !createFormData.admin_contact_number ? '-' : validation.isValid ? '✓' : '✗';
                            return (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                                  <small className="user-form-hint">For SMS/WhatsApp notifications</small>
                                  <small style={{ color: statusColor, fontWeight: '600', fontSize: '0.85rem' }}>
                                    {statusIcon} {createFormData.admin_contact_number ? `${digits.length}/10` : '-'}
                                  </small>
                                </div>
                                {createFormData.admin_contact_number && !validation.isValid && (
                                  <small className="error" style={{ display: 'block', marginTop: '0.25rem' }}>{validation.error}</small>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="admin_contact_number">Contact Number *</label>
                          <input
                            type="tel"
                            id="admin_contact_number"
                            name="admin_contact_number"
                            value={createFormData.admin_contact_number || ''}
                            onChange={handleCreateFormChange}
                            required
                            maxLength="10"
                            placeholder="e.g., 9876543210"
                          />
                          {(() => {
                            const digits = createFormData.admin_contact_number ? createFormData.admin_contact_number.replace(/\D/g, '') : '';
                            const validation = createFormData.admin_contact_number ? validateIndianPhoneNumber(createFormData.admin_contact_number) : { isValid: true, error: null };
                            const statusColor = createFormData.admin_contact_number && !validation.isValid ? '#d32f2f' : digits.length === 10 ? '#27ae60' : '#f59e0b';
                            const statusIcon = !createFormData.admin_contact_number ? '-' : validation.isValid ? '✓' : '✗';
                            return (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                                  <small className="user-form-hint">For SMS/WhatsApp notifications</small>
                                  <small style={{ color: statusColor, fontWeight: '600', fontSize: '0.85rem' }}>
                                    {statusIcon} {createFormData.admin_contact_number ? `${digits.length}/10` : '-'}
                                  </small>
                                </div>
                                {createFormData.admin_contact_number && !validation.isValid && (
                                  <small className="error" style={{ display: 'block', marginTop: '0.25rem' }}>{validation.error}</small>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        <div className="form-group" />
                      </div>
                    )}

                    <div className="user-form-section-title">Account Credentials</div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="admin_username">Username *</label>
                        <input
                          type="text"
                          id="admin_username"
                          name="admin_username"
                          value={createFormData.admin_username}
                          onChange={handleCreateFormChange}
                          required
                          placeholder="john.doe"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="admin_password">Password (system-generated)</label>
                        <input
                          type="password"
                          id="admin_password"
                          name="admin_password"
                          value=""
                          disabled
                          className="user-form-password-input"
                          placeholder="Temporary password sent after email verification"
                        />
                        <small className="user-form-hint">
                          A temporary password will be generated and sent via email after the user verifies their email address
                        </small>
                      </div>
                    </div>

                    <div className="user-form-status-toggle is-disabled">
                      <div className="user-form-status-toggle-copy">
                        <span className="user-form-status-toggle-title">Inactive</span>
                        <span className="user-form-status-toggle-hint">
                          New accounts automatically activate when the user verifies their email
                        </span>
                      </div>
                      <button
                        type="button"
                        className="user-form-switch"
                        disabled
                        aria-pressed={false}
                        aria-label="Inactive"
                      >
                        <span className="user-form-switch-knob" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="form-step-create">
                  <h4>Review & Confirm</h4>
                  <p className="step-description">Please review before creating</p>

                  <div className="review-grid-create">
                    <div className="review-section-create">
                      <h5>Location Information</h5>
                      <div className="review-item-create">
                        <span className="review-label-create">Name:</span>
                        <span className="review-value-create">{createFormData.name}</span>
                      </div>
                      <div className="review-item-create">
                        <span className="review-label-create">Location Code:</span>
                        <span className="review-value-create">{createFormData.location_code}</span>
                      </div>
                      <div className="review-item-create">
                        <span className="review-label-create">Contact Email:</span>
                        <span className="review-value-create">{createFormData.contact_email}</span>
                      </div>
                      {createFormData.contact_phone && (
                        <div className="review-item-create">
                          <span className="review-label-create">Contact Phone:</span>
                          <span className="review-value-create">{formatPhoneNumber(createFormData.contact_phone)}</span>
                        </div>
                      )}
                      {createFormData.address && (
                        <div className="review-item-create">
                          <span className="review-label-create">Address:</span>
                          <span className="review-value-create">{createFormData.address}</span>
                        </div>
                      )}
                      {(createFormData.city || createFormData.state || createFormData.country) && (
                        <div className="review-item-create">
                          <span className="review-label-create">City/State/Country:</span>
                          <span className="review-value-create">
                            {[createFormData.city, createFormData.state, createFormData.country].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="review-section-create">
                      <h5>Administrator Account</h5>
                      <div className="review-item-create">
                        <span className="review-label-create">Name:</span>
                        <span className="review-value-create">{createFormData.admin_full_name}</span>
                      </div>
                      <div className="review-item-create">
                        <span className="review-label-create">Email:</span>
                        <span className="review-value-create">{createFormData.admin_email}</span>
                      </div>
                      <div className="review-item-create">
                        <span className="review-label-create">Username:</span>
                        <span className="review-value-create">{createFormData.admin_username}</span>
                      </div>
                      {employeeIdMode === 'manual' && (
                        <div className="review-item-create">
                          <span className="review-label-create">Employee ID:</span>
                          <span className="review-value-create">{createFormData.admin_employee_id}</span>
                        </div>
                      )}
                      {createFormData.admin_contact_number && (
                        <div className="review-item-create">
                          <span className="review-label-create">Contact:</span>
                          <span className="review-value-create">{formatPhoneNumber(createFormData.admin_contact_number)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 4 && (() => {
                const setupComplete = createLocationStatus === 'success' && createUserStatus === 'success';
                const locationAddress = [
                  createFormData.address,
                  [createFormData.city, createFormData.state, createFormData.country].filter(Boolean).join(', '),
                ].filter(Boolean).join(' · ');

                return (
                  <div className="form-step-create form-step-create--confirm">
                    <h4>{setupComplete ? 'Setup Complete' : 'Confirm & Create'}</h4>
                    {!setupComplete && (
                      <p className="step-description">Creating location and administrator account</p>
                    )}

                    <div className="create-confirm-grid">
                      <div
                        className={[
                          'create-confirm-card',
                          (createLocationStatus === 'success') ? 'is-completed' : '',
                          (createLocationStatus === 'running' || (createLocationStatus === 'idle' && !createdLocationId)) ? 'is-current' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        <div className="create-confirm-header">
                          <div className="create-confirm-title">Location</div>
                          <div className={`create-confirm-status status-${createLocationStatus}`}>
                            {createLocationStatus === 'idle' && 'Pending'}
                            {createLocationStatus === 'running' && 'Running'}
                            {createLocationStatus === 'success' && 'Success'}
                            {createLocationStatus === 'error' && 'Failed'}
                          </div>
                        </div>
                        <div className="create-confirm-body">
                          <div className="create-confirm-facts">
                            <ConfirmFact label="Name" value={createFormData.name} />
                            <ConfirmFact label="Code" value={createFormData.location_code} />
                            <ConfirmFact label="Email" value={createFormData.contact_email} />
                            <ConfirmFact
                              label="Phone"
                              value={createFormData.contact_phone ? formatPhoneNumber(createFormData.contact_phone) : null}
                            />
                            <ConfirmFact label="Address" value={locationAddress || null} wide />
                          </div>
                          {createLocationStatus === 'error' && createLocationError && (
                            <div className="create-confirm-inline-message is-error">{createLocationError}</div>
                          )}
                        </div>
                        {createLocationStatus === 'error' && (
                          <div className="create-confirm-actions">
                            <button
                              type="button"
                              className="action-btn action-secondary action-btn--compact"
                              onClick={() => {
                                goToStepForEdit(1);
                              }}
                              disabled={isCreating}
                            >
                              <span className="action-text">Edit Location Details</span>
                            </button>
                          </div>
                        )}
                      </div>

                      <div
                        className={[
                          'create-confirm-card',
                          (createUserStatus === 'success') ? 'is-completed' : '',
                          (createUserStatus === 'error') ? 'is-error' : '',
                          (createLocationStatus !== 'success') ? 'is-next' : '',
                          (createLocationStatus === 'success' && (createUserStatus === 'running' || createUserStatus === 'idle' || createUserStatus === 'error')) ? 'is-current' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        <div className="create-confirm-header">
                          <div className="create-confirm-title">Admin User</div>
                          <div className={`create-confirm-status status-${createUserStatus}`}>
                            {createUserStatus === 'idle' && (createLocationStatus === 'success' ? 'Pending' : 'Blocked')}
                            {createUserStatus === 'running' && 'Running'}
                            {createUserStatus === 'success' && 'Success'}
                            {createUserStatus === 'error' && 'Failed'}
                          </div>
                        </div>
                        <div className="create-confirm-body">
                          <div className="create-confirm-facts">
                            <ConfirmFact label="Name" value={createFormData.admin_full_name} />
                            <ConfirmFact label="Email" value={createFormData.admin_email} />
                            <ConfirmFact label="Username" value={createFormData.admin_username} />
                            {employeeIdMode === 'manual' && (
                              <ConfirmFact label="Employee ID" value={createFormData.admin_employee_id} />
                            )}
                            <ConfirmFact
                              label="Contact"
                              value={createFormData.admin_contact_number ? formatPhoneNumber(createFormData.admin_contact_number) : null}
                            />
                          </div>
                          {createUserStatus === 'success' && !setupComplete && (
                            <div className="create-confirm-inline-message is-success">
                              Verification email will be sent to {createFormData.admin_email}.
                            </div>
                          )}
                          {createUserStatus === 'error' && createUserError && (
                            <div className="create-confirm-inline-message is-error">{createUserError}</div>
                          )}
                        </div>
                        {(createLocationStatus === 'success' && createUserStatus === 'error') && (
                          <div className="create-confirm-actions">
                            <button
                              type="button"
                              className="action-btn action-secondary action-btn--compact"
                              onClick={() => {
                                goToStepForEdit(2);
                              }}
                              disabled={isCreating}
                            >
                              <span className="action-text">Edit Admin User Details</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {setupComplete && (
                      <div className="create-confirm-note is-success">
                        Setup complete. Remind the admin to configure Complaint TAT under Settings before go-live.
                      </div>
                    )}
                    {(createLocationStatus === 'success' && createUserStatus === 'error') && (
                      <div className="create-confirm-note is-info">
                        You can finish now, or edit the admin user details and try again.
                      </div>
                    )}
                  </div>
                );
              })()}
          </form>
        </div>

        <div className={`locations-create-modal-footer${currentStep > 1 && currentStep < 4 ? ' locations-create-modal-footer--split' : ''}`}>
          {currentStep > 1 && currentStep < 4 && (
            <div className="locations-create-modal-footer-start">
              <button
                type="button"
                className="action-btn action-secondary"
                onClick={handleBack}
                disabled={isCreating || createLocationStatus === 'running' || createUserStatus === 'running'}
              >
                <span className="action-text">Back</span>
              </button>
            </div>
          )}
          <div className="locations-create-modal-footer-end">
            {(currentStep !== 4 || createLocationStatus === 'error') && (
              <button
                type="button"
                className="action-btn action-secondary"
                onClick={handleCancelCreate}
                disabled={isCreating}
              >
                <span className="action-text">Cancel</span>
              </button>
            )}

            {currentStep < 3 ? (
              <button
                type="button"
                className="action-btn action-primary"
                onClick={handleNext}
              >
                <span className="action-text">Next</span>
              </button>
            ) : currentStep === 3 ? (
              <button
                type="button"
                className="action-btn action-primary"
                disabled={isCreating}
                onClick={handleContinueToConfirm}
              >
                <span className="action-text">Continue to Confirm</span>
              </button>
            ) : (
              <button
                type="button"
                className="action-btn action-primary"
                onClick={handleFinish}
                disabled={createLocationStatus !== 'success' || (createUserStatus !== 'success' && createUserStatus !== 'error')}
              >
                <span className="action-text">Finish</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
